import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../../app/providers/AuthProvider';
import { useReportsConfig } from '../../hooks/useReportsConfig';
import ReportViewer from '../../components/ReportViewer';
import { LoadingBlock, EmptyState } from '../../../../shared/ui/composites';
import { cn } from '../../../../lib/utils';

function sortByOrder(arr) {
  return [...arr].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function pickDefaultId(allReports, defaultReportId) {
  if (defaultReportId && allReports.some((r) => r.id === defaultReportId)) {
    return defaultReportId;
  }
  const flagged = allReports.find((r) => r.is_main_dashboard);
  if (flagged) return flagged.id;
  return allReports[0]?.id || null;
}

export default function ReportsPage() {
  const { profile, loading: authLoading } = useAuth();
  const { data, isLoading, isError, refetch, isFetching } = useReportsConfig({
    profile,
    enabled: !!profile,
  });
  const [activeReport, setActiveReport] = useState(null);

  const parsed = data?.parsed;
  const customReports = useMemo(() => {
    if (!parsed) return [];
    return parsed.reports.filter((r) => r.visible !== false);
  }, [parsed]);
  const defaultReportId = parsed?.defaultReportId || null;
  const loading = authLoading || isLoading;

  const allReports = useMemo(() => sortByOrder(customReports), [customReports]);

  useEffect(() => {
    if (loading || isError) return;
    setActiveReport((prev) => {
      if (prev && allReports.some((r) => r.id === prev)) return prev;
      return pickDefaultId(allReports, defaultReportId);
    });
  }, [loading, isError, allReports, defaultReportId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6 rtl">
        <LoadingBlock />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-background p-[var(--content-padding)] rtl max-md:p-3">
        <header className="mb-4 rounded-[18px] border-b-2 border-indigo-200 bg-[#eef2ff] px-8 py-5 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-800">گزارشات</h1>
        </header>
        <EmptyState
          message="دریافت گزارشات با خطا مواجه شد."
          actionLabel={isFetching ? 'در حال تلاش…' : 'تلاش مجدد'}
          onAction={() => refetch()}
        />
      </div>
    );
  }

  const current = allReports.find((r) => r.id === activeReport) || allReports[0];

  function sortedTabs(report) {
    if (!report?.tabs) return [];
    return sortByOrder(report.tabs.filter((t) => t.visible !== false));
  }

  return (
    <div className="min-h-screen w-full bg-background p-[var(--content-padding)] rtl max-md:p-3">
      <div className="w-full min-w-0 overflow-hidden rounded-[18px] bg-card shadow-sm ring-1 ring-slate-200/70">
        {current && (
          <ReportViewer
            key={current.id}
            title="گزارشات"
            actions={
              allReports.length > 1 ? (
                <div className="flex flex-wrap gap-2">
                  {allReports.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setActiveReport(r.id)}
                      className={cn(
                        'rounded-md px-2 py-0.5 text-xs font-medium transition-colors',
                        current?.id === r.id
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                      )}
                    >
                      {r.name}
                      {r.id === defaultReportId && (
                        <span className="mr-1.5 rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] opacity-70">
                          پیش‌فرض
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ) : null
            }
            report={{ ...current, tabs: sortedTabs(current) }}
            profile={profile}
          />
        )}

        {!current && (
          <EmptyState message="هیچ گزارشی برای نقش شما تنظیم نشده است" />
        )}
      </div>
    </div>
  );
}
